import { useAuth } from '../contexts/AuthContext';

export const usePermissions = () => {
  const { user } = useAuth();
  
  const hasRole = (roleName: string): boolean => {
    // User has array of roles, not a single role
    return user?.roles?.some((role: any) => 
      role.name?.toLowerCase() === roleName.toLowerCase()
    ) || false;
  };
  
  const hasAnyRole = (roleNames: string[]): boolean => {
    return roleNames.some(role => hasRole(role));
  };

  const hasPermission = (permissionName: string): boolean => {
    return user?.roles?.some((role: any) => 
      role.permissions?.some((permission: any) => 
        permission.name === permissionName
      )
    ) || false;
  };
  
  const permissions = {
    // User management
    canManageUsers: hasAnyRole(['admin', 'hr']),
    canViewUsers: hasAnyRole(['admin', 'hr', 'manager']),
    
    // Leave management
    canApproveLeaves: hasAnyRole(['admin', 'hr', 'manager']),
    canViewAllLeaves: hasAnyRole(['admin', 'hr', 'manager']),
    canCreateLeave: true, // All authenticated users
    
    // Reports
    canViewReports: hasAnyRole(['admin', 'hr', 'manager']),
    canExportReports: hasAnyRole(['admin', 'hr']),
    
    // Team management
    canViewTeamCalendar: hasAnyRole(['admin', 'hr', 'manager']),
    
    // PIM management
    canManagePim: hasPermission('pim.manage') || hasRole('admin'),
    canReadProducts: hasPermission('pim.products.read') || hasPermission('pim.manage') || hasRole('admin'),
    canWriteProducts: hasPermission('pim.products.write') || hasPermission('pim.manage') || hasRole('admin'),
    canDeleteProducts: hasPermission('pim.products.delete') || hasPermission('pim.manage') || hasRole('admin'),
    canManageAttributes: hasPermission('pim.attributes.manage') || hasPermission('pim.manage') || hasRole('admin'),
    canManageProductTypes: hasPermission('pim.product_types.manage') || hasPermission('pim.manage') || hasRole('admin'),
    canManageApiKeys: hasPermission('pim.api.read') || hasPermission('pim.manage') || hasRole('admin'),
    
    // System administration
    canManageRoles: hasRole('admin'),
    isAdmin: hasRole('admin'),
    isHR: hasRole('hr'),
    isManager: hasRole('manager'),
    isEmployee: hasRole('employee'),
  };
  
  return {
    ...permissions,
    hasRole,
    hasAnyRole,
    hasPermission,
    user,
  };
};